import React, { createElement, useState } from 'react';
import { useIntl } from 'react-intl';
import { MdCheck } from 'react-icons/md';
import type { EntitlementResponse } from '@api/types/entitlements';
import {
  RUNNER_ICONS,
  RUNNER_LABELS,
  skillAction,
  skillActionLabel,
  skillIcon,
} from '@components/Marketplace/marketplaceSkills';
import {
  Check,
  Chip,
  Face,
  Glyph,
  Name,
  PillButton,
  Reveal,
  RevealRow,
  RevealText,
  TileRoot,
} from './FooterTile.styled';

interface FooterSkillTileProps {
  readonly skill: EntitlementResponse;
  readonly grow: boolean;
  readonly onDownload: (skill: EntitlementResponse) => void;
}

/** A tap opens the details on touch screens, where there is no hover. */
const FooterSkillTile: React.FC<FooterSkillTileProps> = ({ skill, grow, onDownload }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const action = skillAction(skill);
  const actionLabel = skillActionLabel(intl, skill);
  const enabledLabel = intl.formatMessage({ id: 'appsFooter.skill.enabled', defaultMessage: 'You have it' });

  const runAction = () => {
    if (action.kind === 'download') onDownload(skill);
    if (action.kind === 'contactSales') window.open(action.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <TileRoot
      $grow={grow}
      data-open={open || undefined}
      onMouseLeave={() => setOpen(false)}
      data-testid={`apps-footer__skill-${skill.skillId}`}
    >
      <Face
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={[skill.displayName, skill.enabled && enabledLabel, skill.description]
          .filter(Boolean)
          .join('. ')}
      >
        {skill.enabled && (
          <Check aria-hidden="true">
            <MdCheck size={22} />
          </Check>
        )}
        <Glyph aria-hidden="true">{createElement(skillIcon(skill), { size: 48 })}</Glyph>
        <Name aria-hidden="true">{skill.displayName}</Name>
      </Face>
      <Reveal>
        <RevealText aria-hidden="true">{skill.description}</RevealText>
        {skill.supportedRunners.length > 0 && (
          <RevealRow aria-hidden="true">
            {skill.supportedRunners.map((mode) => (
              <Chip key={mode}>
                {createElement(RUNNER_ICONS[mode], { size: 13 })}
                {intl.formatMessage(RUNNER_LABELS[mode])}
              </Chip>
            ))}
          </RevealRow>
        )}
        {action.kind === 'none' ? (
          <Chip>{actionLabel}</Chip>
        ) : (
          <PillButton
            type="button"
            $primary
            onClick={runAction}
            data-testid={`apps-footer__skill-action-${skill.skillId}`}
          >
            {actionLabel}
          </PillButton>
        )}
      </Reveal>
    </TileRoot>
  );
};

export default FooterSkillTile;
